export type StatusHighlight = "success" | "error" | "warning" | "neutral";

export type OperatorStatusRow = {
  label: string;
  value: string;
  highlight?: StatusHighlight;
};

export type OperatorStatusData = {
  payment_status?: string;
  payout_status?: string;
  provider?: string;
  operator?: string;
  operator_lookup_id?: string;
  operator_status?: string;
  operator_status_normalized?: string;
  operator_response?: unknown;
  read_only?: boolean;
  message?: string;
  side?: string;
  country?: string;
  payment_id?: string;
  payment_type?: string;
  error?: string;
};

const STATUS_LABELS: Record<string, string> = {
  SUCCEEDED: "Réussi",
  SUCCESS: "Réussi",
  SUCCESSFUL: "Réussi",
  SUCCESSFULL: "Réussi",
  COMPLETED: "Terminé",
  FAILED: "Échoué",
  CANCELED: "Annulé",
  CANCELLED: "Annulé",
  PENDING: "En attente",
  PROCESSING: "En cours",
  INITIATED: "Initié",
  EXPIRED: "Expiré",
};

const REASON_LABELS: Record<string, string> = {
  NOT_ENOUGH_FUNDS: "Fonds insuffisants sur le compte marchand",
  PAYEE_NOT_FOUND: "Bénéficiaire introuvable",
  PAYER_NOT_FOUND: "Payeur introuvable",
  TIMEOUT: "Délai dépassé",
  APPROVAL_REJECTED: "Paiement refusé par l'utilisateur",
  INTERNAL_PROCESSING_ERROR: "Erreur interne opérateur",
  COULD_NOT_PERFORM_TRANSACTION: "Transaction impossible",
  SERVICE_UNAVAILABLE: "Service indisponible",
};

const formatStatus = (value: unknown): string => {
  if (value == null) return "—";
  const key = String(value).toUpperCase();
  return STATUS_LABELS[key] || String(value);
};

const formatReason = (value: unknown): string => {
  if (value == null) return "—";
  const key = String(value).toUpperCase();
  return REASON_LABELS[key] || String(value);
};

const statusHighlight = (value: unknown): StatusHighlight => {
  const key = String(value ?? "").toUpperCase();
  if (["SUCCESS", "SUCCESSFUL", "SUCCESSFULL", "SUCCEEDED", "COMPLETED"].includes(key)) return "success";
  if (["FAILED", "CANCELED", "CANCELLED", "EXPIRED", "REJECTED"].includes(key)) return "error";
  if (["PENDING", "PROCESSING", "INITIATED"].includes(key)) return "warning";
  return "neutral";
};

const formatPhone = (value: unknown): string => {
  if (value == null) return "—";
  const phone = String(value);
  if (phone.startsWith("237") && phone.length === 12) {
    return `+${phone.slice(0, 3)} ${phone.slice(3, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`;
  }
  return phone;
};

const formatAmount = (amount: unknown, currency?: unknown): string => {
  if (amount == null || amount === "") return "—";
  const num = Number(amount);
  const formatted = Number.isFinite(num) ? num.toLocaleString("fr-FR") : String(amount);
  return currency ? `${formatted} ${currency}` : formatted;
};

const pushRow = (rows: OperatorStatusRow[], label: string, value: unknown, highlight?: StatusHighlight) => {
  if (value == null || value === "") return;
  rows.push({ label, value: String(value), highlight });
};

const parseMtnResponse = (resp: Record<string, unknown>): OperatorStatusRow[] => {
  const rows: OperatorStatusRow[] = [];
  const data = resp.data as Record<string, unknown> | undefined;

  pushRow(rows, "Référence", resp.reference_id || data?.externalId);
  if (data && typeof data === "object") {
    const txStatus = data.status;
    if (txStatus != null) {
      rows.push({
        label: "Statut transaction",
        value: formatStatus(txStatus),
        highlight: statusHighlight(txStatus),
      });
    }
    pushRow(rows, "Montant", formatAmount(data.amount, data.currency));
    const payee = data.payee as Record<string, unknown> | undefined;
    if (payee?.partyId) pushRow(rows, "Bénéficiaire", formatPhone(payee.partyId));
    const payer = data.payer as Record<string, unknown> | undefined;
    if (payer?.partyId) pushRow(rows, "Payeur", formatPhone(payer.partyId));
    if (data.reason) {
      rows.push({
        label: "Motif",
        value: formatReason(data.reason),
        highlight: "error",
      });
    }
    pushRow(rows, "ID financier", data.financialTransactionId);
    pushRow(rows, "Message émetteur", data.payerMessage);
    pushRow(rows, "Note bénéficiaire", data.payeeNote);
  }
  pushRow(rows, "Message API", resp.message);
  pushRow(rows, "Code réponse", resp.code);
  return rows;
};

const parseOrangeResponse = (resp: Record<string, unknown>): OperatorStatusRow[] => {
  const rows: OperatorStatusRow[] = [];
  const data = resp.data;

  if (typeof data === "string") {
    pushRow(rows, "Erreur", data, "error");
  } else if (data && typeof data === "object") {
    const payload = data as Record<string, unknown>;
    const txStatus = payload.status ?? payload.transactionStatus ?? payload.TransactionStatus ?? payload.State;
    if (txStatus != null) {
      rows.push({
        label: "Statut transaction",
        value: formatStatus(txStatus),
        highlight: statusHighlight(txStatus),
      });
    }
    pushRow(rows, "Référence", payload.messageId ?? payload.MessageId ?? payload.txnid ?? payload.id);
    pushRow(rows, "Montant", formatAmount(payload.amount ?? payload.Amount, payload.currency ?? "XAF"));
    pushRow(
      rows,
      "Bénéficiaire",
      formatPhone(payload.recipient ?? payload.phone ?? payload.msisdn ?? payload.ReceiverNumber)
    );
    pushRow(rows, "Motif", formatReason(payload.reason ?? payload.description ?? payload.message));
  }
  pushRow(rows, "Message API", resp.message);
  pushRow(rows, "Code HTTP", resp.status_code ?? resp.code);
  return rows;
};

const parseSwychrResponse = (resp: Record<string, unknown>): OperatorStatusRow[] => {
  const rows: OperatorStatusRow[] = [];
  const rootData = resp.data as Record<string, unknown> | undefined;
  const nested = rootData?.data as Record<string, unknown> | undefined;
  const attrs = (nested?.attributes ?? rootData?.attributes ?? rootData) as Record<string, unknown> | undefined;

  if (attrs && typeof attrs === "object") {
    const txStatus = attrs.status ?? rootData?.status ?? resp.status;
    if (txStatus != null) {
      rows.push({
        label: "Statut transaction",
        value: formatStatus(txStatus),
        highlight: statusHighlight(txStatus),
      });
    }
    pushRow(rows, "Référence", attrs.transaction_id ?? attrs.reference ?? attrs.payment_uuid ?? attrs.id);
    pushRow(
      rows,
      "Montant",
      formatAmount(
        attrs.amount ?? attrs.net_payable ?? attrs.payout_amount,
        attrs.currency_code ?? attrs.currency ?? "XAF"
      )
    );
    pushRow(rows, "Pays", attrs.country_code);
    pushRow(rows, "Payeur", attrs.name ?? attrs.payer_name);
    pushRow(rows, "Téléphone", formatPhone(attrs.mobile ?? attrs.payer_mobile ?? attrs.phone));
    pushRow(rows, "Email", attrs.email ?? attrs.payer_email);
    pushRow(rows, "Description", attrs.description);
    pushRow(rows, "Motif", formatReason(attrs.failure_reason ?? attrs.reason ?? attrs.message));
  } else if (rootData && typeof rootData === "object") {
    const txStatus = rootData.status;
    if (txStatus != null) {
      rows.push({
        label: "Statut transaction",
        value: formatStatus(txStatus),
        highlight: statusHighlight(txStatus),
      });
    }
    pushRow(rows, "Référence", rootData.transaction_id);
    pushRow(rows, "Montant", formatAmount(rootData.amount, rootData.currency));
  }
  pushRow(rows, "Message API", resp.message);
  return rows;
};

const parseGenericResponse = (resp: Record<string, unknown>): OperatorStatusRow[] => {
  const rows: OperatorStatusRow[] = [];
  const skipKeys = new Set(["data", "status"]);

  Object.entries(resp).forEach(([key, value]) => {
    if (skipKeys.has(key) || value == null) return;
    if (typeof value === "object") return;
    const label = key.replace(/_/g, " ");
    pushRow(rows, label.charAt(0).toUpperCase() + label.slice(1), value);
  });

  const data = resp.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
      if (value == null || typeof value === "object") return;
      const label = key.replace(/_/g, " ");
      if (key.toLowerCase() === "status") {
        rows.push({
          label: "Statut transaction",
          value: formatStatus(value),
          highlight: statusHighlight(value),
        });
      } else if (key.toLowerCase() === "reason") {
        rows.push({ label: "Motif", value: formatReason(value), highlight: "error" });
      } else {
        pushRow(rows, label.charAt(0).toUpperCase() + label.slice(1), value);
      }
    });
  }

  return rows;
};

export const parseOperatorResponseRows = (operatorResponse: unknown, provider?: string): OperatorStatusRow[] => {
  if (!operatorResponse || typeof operatorResponse !== "object") return [];

  const resp = operatorResponse as Record<string, unknown>;
  const providerLower = (provider || "").toLowerCase();

  if (providerLower.includes("mtn") || resp.reference_id != null) {
    const rows = parseMtnResponse(resp);
    if (rows.length > 0) return rows;
  }
  if (providerLower.includes("orange") || resp.status_code != null) {
    const rows = parseOrangeResponse(resp);
    if (rows.length > 0) return rows;
  }
  if (providerLower.includes("swychr")) {
    const rows = parseSwychrResponse(resp);
    if (rows.length > 0) return rows;
  }

  return parseGenericResponse(resp);
};

export const formatDbStatus = formatStatus;

export const getStatusHighlight = statusHighlight;
